/* https://github.com/mui/material-ui/blob/v5.x/packages/mui-styled-engine-sc/src/StyledEngineProvider/StyledEngineProvider.js
    This injector provides similar function to material-ui provider components. Ensures that SSR version is removed and inserted properly.
  */

import React from 'react'
import { isServer } from './isServer'
import getConfig from 'next/config'

interface InjectAemCMSStylesProps {
  injectFirst?: boolean
  children: React.ReactNode
}

const {
  publicRuntimeConfig: { manifestCss },
} = getConfig()

const aemStylesheetURL = `/tmp/${manifestCss['aem.css']}`

/**
 * Injects CSS dynamically and removes existing stylesheets with the same URL
 * to prevent FOUC (Flash of Unstyled Content). Similar to Material-UI's StyledEngineProvider.
 *
 * @param cssUrl - The URL of the CSS file to inject. Should generally be pulled from manifestCss from getConfig
 * @param injectFirst - Whether to inject the CSS at the beginning of the head (default: false)
 * @param children - React children to render
 *
 * @example
 * ```tsx
 * <InjectAemCMSStyles cssUrl="/styles/theme.css" injectFirst={true}>
 *   <App />
 * </InjectAemCMSStyles>
 * ```
 */
const InjectAemCMSStyles = ({
  injectFirst = false,
  children,
}: InjectAemCMSStylesProps): JSX.Element => {
  if (!isServer()) {
    const head = document.head

    // Find existing link with the same href
    const existingSSRLink = document.querySelector(
      `link[href="${aemStylesheetURL}"]`
    )

    // Removes existing stylesheet immediately before adding the new one
    // This prevents the old styles from being applied when the new one is applied to eliminate flash of unstyled content.
    // This is basically a copy of what jssStyles does in the useEffect in _app does.
    if (existingSSRLink) {
      existingSSRLink.remove()
    }

    const linkElement = document.createElement('link')
    linkElement.rel = 'stylesheet'
    linkElement.href = aemStylesheetURL
    linkElement.setAttribute('data-aem-css', 'true')

    // Add the new stylesheet
    if (injectFirst) {
      head.insertBefore(linkElement, head.firstChild)
    } else {
      head.appendChild(linkElement)
    }
  }

  return <>{children}</>
}

export default InjectAemCMSStyles
